import { Combobox, ComboboxProps, Option, useId } from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInputAppearance } from '../hooks/useInputAppearance';

interface SelectedTime {
  hour: number;
  minute: number;
}

export interface DropDownHoursProps {
  event: Date;
  handleTimeChange: (selectedTime: SelectedTime) => void;
  className?: string;
  'aria-label'?: string;
  /**
   * Visual appearance of the underlying Combobox (e.g. `filled-darker`).
   * Defaults to the theme-based input appearance so it matches sibling
   * inputs like `LocalizedDatePicker`.
   */
  appearance?: ComboboxProps['appearance'];
}

export const pad = (input: number): string => String(input).padStart(2, '0');

/**
 * Returns a list with times users may select as event start/end time
 * in 15 minute steps
 *
 * @param selected if a previous selected time value does not fit those 15
 *  minute steps, we can add this time here, to enable the user to switch
 *  back to the previous value
 */
export const getTimesList = (selected: SelectedTime): string[] => {
  const hours: string[] = [];
  const steps = [45, 30, 15, 0];

  // Is selected already covered by the time steps?
  let includeSelected = !steps.includes(selected.minute);

  for (let index = 23; index >= 0; --index) {
    steps.forEach(min => {
      hours.push(pad(index) + ':' + pad(min));

      if (
        includeSelected &&
        index === selected.hour &&
        (selected.minute < min || selected.minute > 45)
      ) {
        hours.push(pad(selected.hour) + ':' + pad(selected.minute));

        includeSelected = false;
      }
    });
  }
  return hours.reverse();
};

export const dateToTimeString = (date: Date): string =>
  pad(date.getHours()) + ':' + pad(date.getMinutes());

export const DropDownHours: React.FC<DropDownHoursProps> = ({
  event,
  handleTimeChange,
  className,
  'aria-label': ariaLabel,
  appearance,
}) => {
  const { t } = useTranslation();
  const themedAppearance = useInputAppearance();
  const [searchQuery, setSearchQuery] = useState(dateToTimeString(event));
  const [selectedTime, setSelectedTime] = useState<SelectedTime>({
    hour: event.getHours(),
    minute: event.getMinutes(),
  });
  const [isError, setIsError] = useState(false);
  const inputItems = getTimesList(selectedTime);

  useEffect(() => {
    setSelectedTime({
      hour: event.getHours(),
      minute: event.getMinutes(),
    });

    setSearchQuery(dateToTimeString(event));
  }, [event.getHours(), event.getMinutes()]);

  const defaultAriaLabel = t('calendar_time_label', {
    defaultValue:
      'Öffnet einen Flyout, um die Uhrzeit des Termins auszuwählen oder bietet die Möglichkeit, eine Uhrzeit manuell einzugeben',
  });

  const effectiveAriaLabel = ariaLabel || defaultAriaLabel;

  const dropdownId = useId();

  const handleInputChange = (newValue: string) => {
    setSearchQuery(newValue);

    const timeMatch = newValue.match(/^(\d{2}):(\d{2})$/);

    if (!timeMatch) {
      return setIsError(true);
    }

    setIsError(false);

    const newSelectedTime = {
      hour: Number.parseInt(timeMatch[1], 10),
      minute: Number.parseInt(timeMatch[2], 10),
    };

    setSelectedTime(newSelectedTime);
    handleTimeChange(newSelectedTime);
  };

  return (
    <>
      <label
        htmlFor={dropdownId}
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {effectiveAriaLabel}
      </label>
      <Combobox
        id={dropdownId}
        aria-label={effectiveAriaLabel}
        freeform
        data-testid="DropDownHours"
        value={searchQuery}
        selectedOptions={[]}
        onInput={(ev) => handleInputChange((ev.target as HTMLInputElement).value)}
        onOptionSelect={(_ev, data) => {
          if (data.optionText) {
            handleInputChange(data.optionText);
          }
        }}
        className={className}
        appearance={appearance ?? themedAppearance}
        style={isError ? { borderColor: 'red' } : undefined}
      >
        {inputItems.map(item => (
          <Option key={item} value={item}>
            {item}
          </Option>
        ))}
      </Combobox>
    </>
  );
};
